<script lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui';
import type { ColumnPinningState, ColumnSizingState, RowSelectionState, SortingState } from '@tanstack/vue-table';
import type { Component } from 'vue';
import type { AnyItem, CollectionFieldPath, CompositePrimaryKey, GlobalFieldId, PrimitiveType, ResolvedMonospaceCollection } from '#layers/data-model/public';
import type { LayoutSortingState, SharedLayoutEmits, SharedLayoutProps } from '#layers/listing/types/layout';
import type { ColumnHeaderActionGroup, DataTableProps } from '#layers/ui/public';
import {
	getCanonicalFieldEditRoute,
	getCollectionPrimaryKeyFields,
	getItemPrimaryKey,
	isEmptyKey,
	resolveCollectionFieldPath,
	stringifyStableId,
	toCollectionFieldPathKey,
	useWorkspaceSchemaStore,
} from '#layers/data-model/public';
import { DEFAULT_LISTING_PAGE_SIZE, useCursorAnchor, useDeferredTrue } from '#layers/ui/public';

export interface TableDisplayOptions {
	visibleColumnPaths?: CollectionFieldPath[];
	/** Use visibleColumnPaths instead. Kept for backwards compatibility migration. */
	visibleColumnIds?: GlobalFieldId[];
	columnOrder?: string[];
	columnSizing?: ColumnSizingState;
	columnPinning?: ColumnPinningState;
	sorting?: LayoutSortingState;
	pageSize?: number;
}

export interface LayoutTableProps<T> extends SharedLayoutProps<TableDisplayOptions>, DataTableProps<T> {
	renderDisplayTemplate: Component;
}
</script>

<script setup lang="ts" generic="T extends AnyItem = AnyItem">
import { createColumnHelper } from '@tanstack/vue-table';
import { reactiveOmit } from '@vueuse/core';
import { computed, h, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { canEditDataModel, useEntitlement } from '#layers/access/public';
import { SortDirection, virtualizeColumnAutosizeOnScrollFlag } from '#layers/base/public';
import { useItems } from '#layers/collection-query/public';
import { injectFilterSchemaContext, isFilterablePrimitivePath } from '#layers/filters/public';
import LayoutDefaultEmptyState from '#layers/listing/components/Layout/LayoutDefaultEmptyState.vue';
import { injectListingContext } from '#layers/listing/components/Listing/context';
import ListingErrorState from '#layers/listing/components/Listing/ErrorState.vue';
import { useConvertLayoutSortingStateToQuerySort } from '#layers/listing/composables/use-convert-layout-sorting-state-to-query-sort';
import { exposeListingQueryToBlockRootContext } from '#layers/listing/composables/use-listing-query-block-root-context';
import { useOutputGate } from '#layers/listing/composables/use-output-gate';
import { useSelection } from '#layers/listing/composables/use-selection';
import { useSortGate } from '#layers/listing/composables/use-sort-gate';
import AddFieldAction from './AddFieldAction.vue';
import { getStableFilterKey } from './get-stable-filter-key';
import LayoutTableDisplayOptions, { initializeLayoutTableOptions } from './LayoutTableDisplayOptions.vue';
import { listingTablePaginationClass, listingTablePresentationProps } from './listing-table-presentation';
import { migrateTableDisplayOptions } from './migrate-table-display-options';
import { useCellContextMenu } from './use-cell-context-menu';
import { useTableColumns } from './use-table-columns';
import { useTableQuerySelection } from './use-table-query-selection';

defineOptions({
	inheritAttrs: false,
});

const props = defineProps<LayoutTableProps<T>>();

const emit = defineEmits<SharedLayoutEmits>();

const selection = defineModel<CompositePrimaryKey[]>('selection', { default: () => [] });

const LAYOUT_TABLE_VIRTUALIZE_MIN_ROWS = 50;

const { can } = useEntitlement();
const { findFieldById } = useWorkspaceSchemaStore();
const collectionPrimaryKeyFields = computed(() => props.collection ? getCollectionPrimaryKeyFields(props.collection) : []);
const { selectionFields, isSelected, replace, toggleSelection, clear: clearSelection } = useSelection({
	selection,
	primaryKeyFields: collectionPrimaryKeyFields,
	selectionKeys: () => props.selectionKeys,
	projectionKeys: () => props.projectionKeys,
	selectionMode: () => props.selectionMode,
});
const sortGate = useSortGate(() => props.collection);
const outputGate = useOutputGate(() => props.collection);
const displayOptions = computed(() => initializeLayoutTableOptions({ options: props.displayOptions, collection: props.collection, sortDirections: sortGate.directions, allowsPath: outputGate.allowsPath }));

const configuredColumnPaths = computed(() => displayOptions.value.visibleColumnPaths ?? []);

// Saved columns may name fields the user or the list read no longer offers.
const visibleColumnPaths = computed(() => configuredColumnPaths.value.filter((fieldPath) => outputGate.allowsPath(fieldPath)));

const visibleColumnKeys = computed(() => visibleColumnPaths.value.map((fieldPath) => toCollectionFieldPathKey(fieldPath)));

const pageSize = computed({
	get: () => displayOptions.value.pageSize ?? DEFAULT_LISTING_PAGE_SIZE,
	set: (value) => handleDisplayOptionUpdate({ pageSize: value }),
});

const { convert: convertSort } = useConvertLayoutSortingStateToQuerySort(sortGate.directions);

const visibleColumnQuerySelection = useTableQuerySelection(() => props.collection, visibleColumnPaths);

const querySelection = computed(() => {
	const base = visibleColumnPaths.value.length === 0
		? { fields: collectionPrimaryKeyFields.value.map((field) => field.apiName) }
		: visibleColumnQuerySelection.value;

	// Selection keying reads these off each row, so they must be requested even when no column shows them.
	const keyFieldNames = [...collectionPrimaryKeyFields.value, ...selectionFields.value].map((field) => field.apiName);
	if (keyFieldNames.length === 0) return base;

	return { ...base, fields: [...new Set([...keyFieldNames, ...(base.fields ?? [])])] };
});

const { items, pageNumber, totalItemsCount, currentItemsCount, filteredItemsCount, isSettled, showLoading, error, refresh, query: itemsQuery } = useItems({
	collection: () => props.collection,
	errorOnNonPermittedField: () => Boolean(props.selectionKeys?.length || props.projectionKeys?.length),
	query: () => ({
		filter: props.filter,
		...querySelection.value,
		search: props.searchTerm,
		sort: convertSort(displayOptions.value.sorting),
	}),
	pageSize,
});

exposeListingQueryToBlockRootContext(() => ({
	collection: props.collection,
	query: itemsQuery.value,
}));

/**
 * Preserve the difference between "not loaded yet" and "loaded empty" for the
 * virtualized table's initial header sizing behavior.
 *
 * `undefined` hides headers during the first loading gap; `[]` means the query
 * resolved empty, so the shared table can size columns from header labels.
 *
 * Cached rows are withheld once the query fails: they answer whichever query last
 * succeeded, so leaving them up presents them as the result of the failed one and
 * hides the failure behind stale rows. `[]` hands the table over to the `empty`
 * slot, where the error state takes it.
 */
const tableData = computed(() => {
	if (error.value) return [];
	return showLoading.value && items.value.length === 0 ? undefined : items.value;
});
const tableVirtualize = computed(() =>
	tableData.value && tableData.value.length > LAYOUT_TABLE_VIRTUALIZE_MIN_ROWS
		? { minItemSize: 40 }
		: false,
);

const showSpinner = useDeferredTrue(showLoading, 350);

watch(pageNumber, () => {
	clearSelection();
});

const tableProps = reactiveOmit(
	props,
	'collection',
	'displayOptions',
	'filter',
	'searchTerm',
	'selection' as keyof typeof props,
	'selectionMode',
	'emptyState',
);

function handleRowSelectionUpdate(rows: RowSelectionState) {
	const rowsById = new Map(items.value.map((item) => [getRowId(item), item]));

	replace(...Object.keys(rows)
		.filter((id) => rows[id] === true)
		.flatMap((id) => rowsById.get(id) ?? []));
}

const rowSelectionState = computed<RowSelectionState>(() => {
	const state: RowSelectionState = {};

	for (const item of items.value) {
		if (isSelected(item)) state[getRowId(item)] = true;
	}

	return state;
});

function toQuerySortEntry(
	value: { id: string; desc: boolean },
	visibleColumnPaths: CollectionFieldPath[],
	collection: ResolvedMonospaceCollection,
): LayoutSortingState[number] | null {
	const fieldPath = visibleColumnPaths.find((path) => toCollectionFieldPathKey(path) === value.id);
	if (!fieldPath || fieldPath.length !== 1) return null;

	const segment = resolveCollectionFieldPath(collection, fieldPath)?.at(-1);
	// `CollectionFieldPathField` is a discriminated union — the primitive variant has `type`, the
	// relation variant does not — so this narrowing also rejects single-segment relation columns.
	if (!segment || !('type' in segment.field)) return null;

	return { fieldId: segment.field.id, direction: value.desc ? SortDirection.Descending : SortDirection.Ascending };
}

const sortingState = computed<SortingState>({
	get: () => displayOptions.value.sorting?.map(({ fieldId, direction }) => ({ id: fieldId, desc: direction === SortDirection.Descending })) ?? [],
	set: (value) =>
		handleDisplayOptionUpdate({
			sorting: value.flatMap((entry) => {
				const sortEntry = toQuerySortEntry(entry, visibleColumnPaths.value, props.collection);
				return sortEntry ? [sortEntry] : [];
			}),
		}),
});

function getRowId(row: T) {
	return stringifyStableId(getItemPrimaryKey(row, props.collection));
}

const columnVisibility = computed({
	get: () => {
		const visibility: Record<string, boolean> = {};

		for (const fieldPath of visibleColumnPaths.value) {
			visibility[toCollectionFieldPathKey(fieldPath)] = true;
		}

		return visibility;
	},
	set: (value) => {
		const nextVisibleColumnPaths = visibleColumnPaths.value.filter((fieldPath) => value[toCollectionFieldPathKey(fieldPath)] !== false);

		handleDisplayOptionUpdate({ visibleColumnPaths: nextVisibleColumnPaths });
	},
});

const listingContext = injectListingContext();
listingContext.setDisplayOptionsComponent(LayoutTableDisplayOptions);

const filterSchema = injectFilterSchemaContext();
const { t } = useI18n();
const filterSchemaCollection = computed(() => filterSchema.findCollectionById(props.collection.id));

const columnHelper = createColumnHelper<T>();

const { open: cellMenuOpen, items: cellMenuItems, reference: cellMenuReference, handleContextMenu: handleCellContextMenu } = useCellMenu();

const { fieldColumns } = useTableColumns<T>({
	collection: () => props.collection,
	renderDisplayTemplate: () => props.renderDisplayTemplate,
	visibleColumnPaths,
	getHeaderActions,
	onCellContextMenu: handleCellContextMenu,
	sortDirections: sortGate.directions,
});

function getHeaderActions(fieldPath: CollectionFieldPath): ColumnHeaderActionGroup[] | undefined {
	const groups: ColumnHeaderActionGroup[] = [];

	const fieldFilters = listingContext.fieldFilters;
	const filterable = fieldFilters
		&& filterSchemaCollection.value
		&& isFilterablePrimitivePath(fieldPath, filterSchemaCollection.value, filterSchema)
		&& fieldFilters.resolve(fieldPath);

	if (filterable) {
		groups.push({
			items: [{
				label: t('filters.filter_column'),
				icon: 'lucide:list-filter',
				onSelect: () => fieldFilters.add(filterable),
			}],
		});
	}

	if (can(canEditDataModel)) {
		const terminalFieldId = resolveCollectionFieldPath(props.collection, fieldPath)?.at(-1)?.field.id;
		const terminalField = terminalFieldId ? findFieldById(terminalFieldId) : undefined;

		if (terminalField) {
			groups.push({
				anchor: 'end',
				items: [{
					label: t('pages.data_model.edit_field'),
					icon: 'lucide:pencil',
					to: getCanonicalFieldEditRoute(terminalField),
				}],
			});
		}
	}

	return groups.length > 0 ? groups : undefined;
}

function useCellMenu() {
	const menu = useCellContextMenu(() => props.collection);
	const open = ref(false);
	const target = ref<{ fieldPath: CollectionFieldPath; value: unknown; type: PrimitiveType } | null>(null);
	const { reference, anchorTo } = useCursorAnchor();

	const items = computed<DropdownMenuItem[]>(() =>
		target.value ? menu.buildCellMenuItems(target.value) ?? [] : [],
	);

	function handleContextMenu(fieldPath: CollectionFieldPath, value: unknown, type: PrimitiveType, e: MouseEvent) {
		menu.resetCopied();
		target.value = { fieldPath, value, type };

		if (items.value.length === 0) {
			target.value = null; // no menu → let the native browser context menu show
			return;
		}

		e.preventDefault();
		anchorTo(e);
		open.value = true;
	}

	return { open, items, reference, handleContextMenu };
}

const columns = computed(() => [
	...fieldColumns.value,
	columnHelper.display({
		id: '$add-field',
		enableResizing: false,
		enableOrdering: false,
		size: 72,
		meta: {
			class: {
				th: 'justify-end',
			},
		},
		header: () => h(AddFieldAction, {
			collectionId: props.collection.id,
			visibleColumnPaths: visibleColumnPaths.value,
			onSelect: (fieldPath) => {
				handleDisplayOptionUpdate({
					visibleColumnPaths: [...visibleColumnPaths.value, fieldPath],
				});
			},
		}),
	}),
]);

const filterKey = computed(() => getStableFilterKey(props.filter));

function handleClick(row: { original: T }) {
	const rowId = getItemPrimaryKey(row.original, props.collection);

	if (isEmptyKey(rowId)) {
		return;
	}

	if (props.selectionMode || selection.value.length > 0) {
		toggleSelection(row.original);
		return;
	}

	emit('open', rowId);
}

/**
 * Resets shared virtualized column floors only when the listing data set or
 * layout meaningfully changes, not while rows recycle during scroll.
 *
 * `props.filter` is serialized: the surrounding form pipeline frequently
 * rewraps the filter object without materially changing it, and including
 * the raw reference made every parent render re-trigger a relax/freeze pass.
 */
const stabilizeColumnSizesDeps = computed(() => [
	props.collection.id,
	visibleColumnKeys.value,
	displayOptions.value.columnOrder ?? [],
	pageNumber.value,
	props.searchTerm ?? '',
	filterKey.value,
	showLoading.value,
]);

const mergedProps = computed(() => ({
	...tableProps,
	...listingTablePresentationProps,
	getRowId,
	rowSelection: rowSelectionState.value,
	rowSelectionOptions: {
		enableMultiRowSelection: !props.selectionMode || props.selectionMode === 'multiple',
	},
	columnOrder: displayOptions.value.columnOrder,
	columnPinning: displayOptions.value.columnPinning,
	columnSizing: displayOptions.value.columnSizing,
	stabilizeColumnSizesDeps: stabilizeColumnSizesDeps.value,
	virtualizeAutoSizeOnScroll: virtualizeColumnAutosizeOnScrollFlag.value,
	virtualizeColumns: true,
	// Listing rows are a fixed 40px (see the `tr` height below), so use the
	// RecycleScroller path to avoid per-row measurement/layout thrash on scroll.
	fixedRowHeight: 40,
}));

function handleDisplayOptionUpdate(updates: Partial<TableDisplayOptions>) {
	// Migrate the raw props rather than spreading the canonicalized `displayOptions` computed so we
	// do not synthesize keys (sorting, visibleColumnPaths) that the parent never set.
	emit('update:displayOptions', { ...migrateTableDisplayOptions(props.displayOptions), ...updates });
}
</script>

<template>
	<DDataTable
		v-bind="mergedProps"
		v-model:sorting="sortingState"
		v-model:column-visibility="columnVisibility"
		:columns
		:on-select="isSettled ? handleClick : undefined"
		:data="tableData"
		can-select
		can-sort
		hide-pagination
		manual-sorting
		:virtualize="tableVirtualize"
		@update:row-selection="handleRowSelectionUpdate"
		@update:column-order="(columnOrder) => handleDisplayOptionUpdate({ columnOrder: columnOrder as string[] | undefined })"
		@update:column-pinning="(columnPinning) => handleDisplayOptionUpdate({ columnPinning })"
		@update:column-sizing="(columnSizing) => handleDisplayOptionUpdate({ columnSizing })"
	>
		<template #empty>
			<DSpinner v-if="showSpinner" class="size-6 text-muted" />
			<span v-else-if="showLoading" data-testid="loading-placeholder" class="size-6" aria-hidden="true" />
			<ListingErrorState
				v-else-if="error"
				:error
				:can-reset-view
				@retry="refresh"
				@reset-view="$emit('resetView')"
			/>
			<LayoutDefaultEmptyState
				v-else
				:total-items-count
				:filtered-items-count
				:empty-state
				@reset-filters="$emit('resetFilters')"
				@empty-state-action="$emit('emptyStateAction')"
			/>
		</template>

		<template #footer>
			<DPaginationBar
				v-model:page="pageNumber"
				v-model:page-size="pageSize"
				:current-items="currentItemsCount"
				:total-items="totalItemsCount"
				:loading="showLoading"
				:class="listingTablePaginationClass"
			/>
		</template>
	</DDataTable>

	<DDropdownMenu
		v-model:open="cellMenuOpen"
		:items="cellMenuItems"
		:content="{ reference: cellMenuReference }"
	/>
</template>
